import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Store, ExternalLink, Save, Globe } from 'lucide-react';

const THEME_OPTIONS = [
  { value: 'salespro', label: 'SalesPRO (Esmeralda + Oro)' },
  { value: 'fya', label: 'FyA (Carbón / Rojo / Plata / Azul)' },
  { value: 'inventory', label: 'Mi Inventario (Violeta / Cian)' },
];

export default function StoreConfig() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [slug, setSlug] = useState('');
  const [cfg, setCfg] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try { const res = await api.getCompanies(); setCompanies(res.data || []); if (res.data?.[0]) setCompanyId(res.data[0].company_id); } catch { /* noop */ }
  })(); }, []);

  useEffect(() => { if (companyId) loadConfig(companyId); /* eslint-disable-next-line */ }, [companyId]);

  const loadConfig = async (cid) => {
    setLoading(true);
    try {
      const res = await api.getStoreConfig(cid);
      setCfg(res.data.store_config);
      setSlug(res.data.slug || '');
      setCategories(res.data.categories || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al cargar configuración');
    } finally { setLoading(false); }
  };

  const toggleCat = (id) => setCfg(c => {
    const list = new Set(c.visible_category_ids || []);
    if (list.has(id)) list.delete(id); else list.add(id);
    return { ...c, visible_category_ids: [...list] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.updateStoreConfig(companyId, { slug, store_config: cfg });
      toast.success('SalesPRO: Configuración de tienda guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al guardar');
    } finally { setSaving(false); }
  };

  if (!cfg || loading) return <div className="flex items-center justify-center h-64 text-[#64748B]">Cargando…</div>;

  const inputCls = 'w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm';

  return (
    <div className="space-y-6 max-w-4xl" data-testid="store-config-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22] flex items-center gap-2"><Store /> Tienda Web</h1>
          <p className="text-sm text-[#64748B] mt-1">Configura el sitio de venta público de cada empresa</p>
        </div>
        {slug && (
          <a href={`/tienda/${slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline" data-testid="store-config-preview-link">
            <ExternalLink size={15} /> Ver tienda (/tienda/{slug})
          </a>
        )}
      </div>

      <Card className="border border-[#E2E8F0]"><CardContent className="p-4 space-y-4">
        <div>
          <Label>Empresa</Label>
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} className={inputCls} data-testid="store-config-company">
            {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Globe size={14} /> Identificador de tienda (slug)</Label>
            <Input value={slug} onChange={e => setSlug(e.target.value)} placeholder="ej. fya" data-testid="store-config-slug" />
            <p className="text-[11px] text-[#94A3B8] mt-1">URL pública: /tienda/{slug || '<slug>'}</p>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Globe size={14} /> Dominio personalizado</Label>
            <Input value={cfg.domain || ''} onChange={e => setCfg({ ...cfg, domain: e.target.value })} placeholder="ej. fyams.win" data-testid="store-config-domain" />
            <p className="text-[11px] text-[#94A3B8] mt-1">Al entrar a este dominio se abre esta tienda automáticamente (sin www)</p>
          </div>
          <div>
            <Label>Tema visual</Label>
            <select value={cfg.theme} onChange={e => setCfg({ ...cfg, theme: e.target.value })} className={inputCls} data-testid="store-config-theme">
              {THEME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>WhatsApp de pedidos (con código país)</Label>
            <Input value={cfg.whatsapp_number || ''} onChange={e => setCfg({ ...cfg, whatsapp_number: e.target.value })} placeholder="ej. 584141234567" data-testid="store-config-whatsapp" />
          </div>
          <div>
            <Label>Lista de precios</Label>
            <select value={cfg.price_list} onChange={e => setCfg({ ...cfg, price_list: e.target.value })} className={inputCls} data-testid="store-config-pricelist">
              <option value="A">Precio A</option>
              <option value="B">Precio B</option>
            </select>
          </div>
        </div>

        <div>
          <Label>Título del hero</Label>
          <Input value={cfg.hero_title || ''} onChange={e => setCfg({ ...cfg, hero_title: e.target.value })} placeholder="Bienvenido a nuestra tienda" data-testid="store-config-hero-title" />
        </div>
        <div>
          <Label>Subtítulo del hero</Label>
          <Input value={cfg.hero_subtitle || ''} onChange={e => setCfg({ ...cfg, hero_subtitle: e.target.value })} placeholder="Explora nuestro catálogo…" data-testid="store-config-hero-subtitle" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Correo remitente (email marketing)</Label>
            <Input value={cfg.sender_email || ''} onChange={e => setCfg({ ...cfg, sender_email: e.target.value })} placeholder="ventas@tudominio.com" data-testid="store-config-sender-email" />
            <p className="text-[11px] text-[#94A3B8] mt-1">Debe ser de un dominio verificado en Resend</p>
          </div>
          <div>
            <Label>Nombre del remitente</Label>
            <Input value={cfg.sender_name || ''} onChange={e => setCfg({ ...cfg, sender_name: e.target.value })} placeholder="FyA Multiservicios" data-testid="store-config-sender-name" />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="store-config-active">
            <input type="checkbox" checked={!!cfg.active} onChange={e => setCfg({ ...cfg, active: e.target.checked })} /> Tienda activa
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="store-config-showstock">
            <input type="checkbox" checked={!!cfg.show_out_of_stock} onChange={e => setCfg({ ...cfg, show_out_of_stock: e.target.checked })} /> Mostrar productos con stock 0
          </label>
        </div>

        <div>
          <Label>Departamentos visibles <span className="text-[11px] text-[#94A3B8]">(ninguno marcado = mostrar todos)</span></Label>
          <div className="mt-2 grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-[#E2E8F0] rounded-md p-3" data-testid="store-config-categories">
            {categories.length === 0 && <p className="text-sm text-[#94A3B8]">Esta empresa no tiene categorías.</p>}
            {categories.map(c => (
              <label key={c.category_id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`store-config-cat-${c.category_id}`}>
                <input type="checkbox" checked={(cfg.visible_category_ids || []).includes(c.category_id)} onChange={() => toggleCat(c.category_id)} /> {c.name}
              </label>
            ))}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="w-full sm:w-auto" data-testid="store-config-save">
          <Save size={16} className="mr-1" /> {saving ? 'Guardando…' : 'Guardar configuración'}
        </Button>
      </CardContent></Card>
    </div>
  );
}
